import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/base/PageTransition';
import SEOHead from '../../components/base/SEOHead';

import ToolsOverviewContent from './components/ToolsOverviewContent';
import BrowserContent from './components/BrowserContent';
import SkillsContent from './components/SkillsContent';
import SlashCommandsContent from './components/SlashCommandsContent';
import SubagentsContent from './components/SubagentsContent';

type SectionId =
  | 'overview'
  | 'browser'
  | 'skills'
  | 'slash-commands'
  | 'subagents';

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
  group: string;
}

interface TocItem {
  id: string;
  label: string;
  level: number;
}

const navItems: NavItem[] = [
  { id: 'overview', label: '工具概览', icon: 'ri-layout-grid-line', group: '概览' },
  { id: 'browser', label: '浏览器', icon: 'ri-global-line', group: '内置工具' },
  { id: 'skills', label: '技能（Skills）', icon: 'ri-sparkling-line', group: '技能系统' },
  { id: 'slash-commands', label: '斜杠命令', icon: 'ri-terminal-line', group: '技能系统' },
  { id: 'subagents', label: '子智能体', icon: 'ri-git-branch-line', group: '智能体协作' },
];

const tocMap: Record<SectionId, TocItem[]> = {
  overview: [
    { id: 'ov-toollist', label: '工具清单', level: 1 },
    { id: 'ov-groups', label: '工具组', level: 1 },
    { id: 'ov-profiles', label: '工具配置文件', level: 1 },
    { id: 'ov-byprovider', label: '按提供商配置策略', level: 1 },
    { id: 'ov-flow', label: '推荐的智能体工作流', level: 1 },
    { id: 'ov-security', label: '安全性说明', level: 1 },
  ],
  browser: [
    { id: 'br-profiles', label: '配置文件模式', level: 1 },
    { id: 'br-config', label: '配置示例', level: 1 },
    { id: 'br-multiprofile', label: '多配置文件支持', level: 1 },
    { id: 'br-extension', label: 'Chrome 扩展中继', level: 1 },
    { id: 'br-cli', label: 'CLI 快速参考', level: 1 },
    { id: 'br-cli-nav', label: '导航与页面操作', level: 2 },
    { id: 'br-cli-interact', label: '交互操作', level: 2 },
    { id: 'br-cli-extract', label: '数据提取', level: 2 },
    { id: 'br-cli-wait', label: '等待增强', level: 2 },
    { id: 'br-snapshot', label: '快照与 Ref', level: 1 },
    { id: 'br-debug', label: '调试工作流', level: 1 },
  ],
  skills: [
    { id: 'sk-locations', label: '加载位置与优先级', level: 1 },
    { id: 'sk-single-vs-shared', label: '单智能体 vs 共享', level: 1 },
    { id: 'sk-clawhub', label: 'ClawHub 安装与同步', level: 1 },
    { id: 'sk-format', label: 'SKILL.md 格式', level: 1 },
    { id: 'sk-gates', label: '门控（加载时过滤）', level: 1 },
    { id: 'sk-config-override', label: '配置覆盖', level: 1 },
    { id: 'sk-env', label: '环境变量注入', level: 1 },
  ],
  'slash-commands': [
    { id: 'sc-diff', label: '命令 vs 指令的区别', level: 1 },
    { id: 'sc-config', label: '配置斜杠命令', level: 1 },
    { id: 'sc-list', label: '完整命令列表', level: 1 },
    { id: 'sc-model', label: '模型选择 /model', level: 1 },
    { id: 'sc-debug', label: '调试覆盖 /debug', level: 1 },
    { id: 'sc-config-cmd', label: '配置更新 /config', level: 1 },
    { id: 'sc-platform', label: '平台注意事项', level: 1 },
  ],
  subagents: [
    { id: 'sa-session-key', label: '会话键格式', level: 1 },
    { id: 'sa-slash-cmd', label: '/subagents 命令', level: 1 },
    { id: 'sa-spawn', label: 'sessions_spawn 参数', level: 1 },
    { id: 'sa-auth', label: '认证机制', level: 1 },
    { id: 'sa-announce', label: '通告步骤', level: 1 },
    { id: 'sa-tools-strategy', label: '工具策略配置', level: 1 },
    { id: 'sa-concurrency', label: '并发控制', level: 1 },
    { id: 'sa-limits', label: '限制说明', level: 1 },
  ],
};

const contentMap: Record<SectionId, React.ReactNode> = {
  overview: <ToolsOverviewContent />,
  browser: <BrowserContent />,
  skills: <SkillsContent />,
  'slash-commands': <SlashCommandsContent />,
  subagents: <SubagentsContent />,
};

const groups = ['概览', '内置工具', '技能系统', '智能体协作'];

export default function ToolsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const saved = localStorage.getItem('tools_active_section');
    return (saved as SectionId) || 'overview';
  });
  const [activeToc, setActiveToc] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentToc = tocMap[activeSection];
  const currentNavItem = navItems.find(item => item.id === activeSection);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: '工具系统文档',
    description: '浏览器工具、技能、斜杠命令、子智能体使用指南 - OpenClaw 工具系统完整文档',
    url: `${siteUrl}/tools`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  // 监听导航栏子菜单点击
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tools_active_section' && e.newValue) {
        setActiveSection(e.newValue as SectionId);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'tools_active_section') {
        setActiveSection(detail.key as SectionId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('tools_active_section', activeSection);
    setActiveToc(currentToc[0]?.id || '');
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleScroll = () => {
      const headings = currentToc
        .map((t) => document.getElementById(t.id))
        .filter(Boolean) as HTMLElement[];
      let current = headings[0]?.id || '';
      for (const el of headings) {
        if (el.getBoundingClientRect().top < 120) {
          current = el.id;
        }
      }
      setActiveToc(current);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeSection, currentToc]);

  const handleSectionChange = (e: React.MouseEvent, id: SectionId) => {
    e.preventDefault();
    if (id === activeSection) return;
    setActiveSection(id);
    setMobileMenuOpen(false);
  };

  return (
    <PageTransition>
      <SEOHead
        title="工具系统文档 | 浏览器、技能、斜杠命令使用指南"
        description="浏览器工具、技能、斜杠命令、子智能体使用指南 - OpenClaw 工具系统完整文档"
        keywords="浏览器工具, 斜杠命令, 子智能体, OpenClaw工具, 智能体技能"
        canonical={`${siteUrl}/tools`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Navbar />

        {/* Mobile Section Selector */}
        <div className="md:hidden sticky top-16 z-40 bg-slate-900 border-b border-slate-800">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${currentNavItem?.icon} text-cyan-400`}></i>
              </div>
              <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-slate-900 border-b border-slate-800 max-h-[70vh] overflow-y-auto">
              <nav className="p-3">
                {groups.map((group) => {
                  const items = navItems.filter((n) => n.group === group);
                  if (!items.length) return null;
                  return (
                    <div key={group} className="mb-3">
                      <div className="px-3 py-1.5 text-xs text-slate-500 uppercase tracking-wider font-semibold">
                        {group}
                      </div>
                      {items.map((item) => (
                        <button
                          key={item.id}
                          onClick={(e) => handleSectionChange(e, item.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap mb-0.5 text-left ${
                            activeSection === item.id
                              ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                          }`}
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className={`${item.icon} text-base`}></i>
                          </div>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </nav>
            </div>
          )}
        </div>

        <div className="flex flex-1 pt-16" style={{ height: 'calc(100vh - 0px)' }}>
          {/* Main Content */}
          <main
            ref={contentRef}
            className="flex-1 overflow-y-auto"
            style={{ height: 'calc(100vh - 64px)' }}
          >
            <div className="max-w-6xl mx-auto px-10 md:px-20 py-14 min-h-full">
              {contentMap[activeSection]}
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </PageTransition>
  );
}
